import React, { memo, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Collapsible from 'utils/packages/rnCollapsible';
import { Button, CardView, hitSlop, IconClose, rem, Spacer, } from 'design-system';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import FastImage from 'utils/packages/rnFastImage';
import TouchableOpacity from 'utils/packages/TouchableOpacity';
import { formatTryFor0 } from 'utils';
import { CountdownTimer } from './CountdownTimer';
import EmmaRocket from '../img/emma_rocket.png';
import { usePromoDiscountBanner } from '../hooks';
const DiscountBanner = ({ isInTab, hideClose, containerStyle, cardViewProps, showForTier, withFrame, }) => {
    const colors = useColors();
    const { paddingHorizontalStyle, width } = useAppFrameDimensions(isInTab);
    const imageStyles = useMemo(() => {
        const w = width / 1.3;
        return {
            width: w,
            bottom: rem(-60) * (w / rem(283)),
            right: rem(-129) * (w / rem(283)),
        };
    }, [width]);
    const discountBanner = usePromoDiscountBanner();
    if (!discountBanner ||
        (showForTier && discountBanner.matchedSubscription.tier !== showForTier)) {
        return null;
    }
    const { isDismissed, freeTrialDays, onOpen, onDismiss, availableTo, formattedPrices, period, productName, } = discountBanner;
    let title;
    if (freeTrialDays) {
        title = (<Text Text-20 White>
        {formattedPrices.percentOff}% off {productName}
      </Text>);
    }
    else {
        title = (<Text Numbers-20>
        <Text Numbers-20 White>
          {formattedPrices.discountedPrice}{' '}
        </Text>
        <Text Text-16 White>
          instead of{' '}
        </Text>
        <Text Numbers-20 White strikeThrough>
          {formattedPrices.originalPrice}
        </Text>
      </Text>);
    }
    let description = '';
    if (freeTrialDays) {
        description = (<>
        <Text Numbers-14 White>
          {formattedPrices.discountedPrice.split('/')[0]}{' '}
        </Text>
        <Text Numbers-14 White strikeThrough>
          {formattedPrices.originalPrice.split('/')[0]}
        </Text>
        <Text TextThin-14 White>
          /{formattedPrices.originalPrice.split('/')[1]}{' '}
        </Text>
        <Text TextThin-14 White>
          for the first{' '}
          {formattedPrices.numberOfPeriods > 1
                ? `${formattedPrices.numberOfPeriods} `
                : ''}
          {`\n`}
          {period} after a {freeTrialDays}-day free trial
        </Text>
      </>);
    }
    else if (formattedPrices.numberOfPeriods > 1) {
        description = `Get ${formattedPrices.percentOff}% off ${productName} for\n${formattedPrices.numberOfPeriods} ${period}`;
    }
    else {
        description = `Get ${formattedPrices.percentOff}% off your first ${period} of ${productName}`;
    }
    const cta = freeTrialDays
        ? formatTryFor0(formattedPrices.originalPrice.substring(0, 1))
        : `Get ${formattedPrices.percentOff}% off`;
    return (<View style={withFrame ? paddingHorizontalStyle : null}>
      <Collapsible collapsed={Boolean(isDismissed && !hideClose)}>
        <CardView noMarginTop noMarginBottom withPadding style={[
            { backgroundColor: colors.background.contrast },
            containerStyle,
        ]} {...cardViewProps}>
          {!hideClose && (<TouchableOpacity hoverOnDark onPress={onDismiss} style={styles.cancelButton} hitSlop={hitSlop} testID="cancelButton">
              <IconClose alt color={colors.elements.secondary}/>
            </TouchableOpacity>)}

          <CountdownTimer endDate={availableTo} style={styles.timer} dark/>
          <Spacer.H8 />
          {title}
          <Spacer.H4 />
          <Text TextThin-14 White style={styles.description}>
            {description}
          </Text>
          <Spacer h={12}/>
          <Button square hug frosted onPress={onOpen} title={cta} containerStyle={styles.buttonContainer}/>
          <FastImage source={EmmaRocket} resizeMode="cover" style={[imageStyles, styles.image]}/>
        </CardView>
      </Collapsible>
    </View>);
};
const styles = StyleSheet.create({
    cancelButton: {
        position: 'absolute',
        top: rem(8),
        right: rem(8),
        width: rem(24),
        height: rem(24),
        justifyContent: 'center',
        alignItems: 'center',
    },
    timer: {
        padding: 0,
        transform: [{ scale: rem(36) / rem(44) }],
        transformOrigin: 'left',
    },
    description: {
        maxWidth: rem(234),
    },
    buttonContainer: {
        alignSelf: 'flex-start',
        paddingHorizontal: rem(24),
    },
    image: {
        aspectRatio: rem(283) / rem(204),
        position: 'absolute',
        zIndex: -1,
    },
});
export default memo(DiscountBanner);
